import { useState, useEffect, useCallback } from 'react';
import logger from '@/utils/logger';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Edit, Trash2, Package2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navbar from '@/components/Navbar';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// DetailRow component moved outside to avoid unstable nested component
const DetailRow = ({ label, value }) => {
  if (!value) return null;
  return (
    <div className="py-3 border-b border-stone-100 last:border-0">
      <dt className="text-sm font-medium text-muted-foreground mb-1">{label}</dt>
      <dd className="text-base font-semibold text-foreground">{value}</dd>
    </div>
  );
};

export default function YarnDetail() {
  const { yarnId } = useParams();
  const [yarn, setYarn] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const navigate = useNavigate();

  const loadYarn = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/yarns/${yarnId}`, {
        withCredentials: true
      });
      setYarn(response.data);
    } catch (error) {
      logger.error('Failed to load yarn:', error);
      toast.error('Failed to load yarn');
      navigate('/inventory');
    } finally {
      setLoading(false);
    }
  }, [yarnId, navigate]);

  useEffect(() => {
    loadYarn();
  }, [loadYarn]);

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/yarns/${yarnId}`, {
        withCredentials: true
      });
      toast.success('Yarn deleted successfully');
      navigate('/inventory');
    } catch (error) {
      logger.error('Failed to delete yarn:', error);
      toast.error('Failed to delete yarn');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <div className="text-center">
            <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
            <p className="mt-4 text-muted-foreground">Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!yarn) return null;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Button
          data-testid="back-button"
          variant="ghost"
          onClick={() => navigate('/inventory')}
          className="mb-6 rounded-lg text-stone-600 hover:text-primary hover:bg-rose-50 font-medium"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Inventory
        </Button>

        <div className="rounded-3xl bg-white border border-stone-100 overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
          <div className="aspect-video bg-gradient-to-br from-rose-100 to-orange-100 flex items-center justify-center">
            <Package2 className="h-32 w-32 text-primary" />
          </div>

          <div className="p-8">
            <div className="flex justify-between items-start mb-6">
              <div>
                <h1 className="text-4xl font-heading font-extrabold tracking-tight mb-2">
                  {yarn.brand || 'Unknown Brand'}
                </h1>
                <p className="text-xl text-muted-foreground font-body">
                  {yarn.color || 'Unknown Color'}
                </p>
              </div>
              <div className="flex space-x-2">
                <Button
                  data-testid="edit-button"
                  onClick={() => navigate(`/inventory/${yarnId}/edit`)}
                  className="rounded-full bg-white text-primary border-2 border-primary/20 hover:border-primary/50 hover:bg-rose-50 transition-all duration-300 font-bold"
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </Button>
                <Button
                  data-testid="delete-button"
                  onClick={() => setShowDeleteDialog(true)}
                  variant="ghost"
                  className="rounded-full text-destructive hover:bg-red-50"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </Button>
              </div>
            </div>

            <dl className="space-y-0">
              <DetailRow label="Weight" value={yarn.weight} />
              <DetailRow label="Fiber Content" value={yarn.fiber_content} />
              <DetailRow label="Yardage" value={yarn.yardage} />
              <DetailRow label="Quantity" value={yarn.quantity} />
              <DetailRow label="Hook/Needle Size" value={yarn.hook_size} />
              <DetailRow label="Gauge" value={yarn.gauge} />
              <DetailRow label="Dye Lot" value={yarn.dye_lot} />
              <DetailRow label="Purchase Date" value={yarn.purchase_date} />
              <DetailRow label="Location" value={yarn.location} />
              <DetailRow label="Price" value={yarn.price ? `$${yarn.price}` : null} />
            </dl>
          </div>
        </div>
      </main>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Yarn</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this yarn? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-button">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-button"
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
